import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import URI from 'urijs';
import { upperCase } from 'lodash';

import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { getTabsMainId, getTabsItemIds } from 'Components/utils/tabs/tabs.util';

function getWithIds({ id, items = [] }) {
  const mainId = id || getTabsMainId(items);

  const itemsWithIds = items.map((item, index) => {
    const [headerId, panelId] = getTabsItemIds({
      id: item.headerId,
      index,
      mainId,
      title: item.header && item.header.title,
    });
    return { ...item, headerId, panelId };
  });

  return [mainId, itemsWithIds];
}

/* scroll to Tabs or Accordion item #id and open it */
export function withOpenAndScrollToHash(WrappedComponent) {
  return function OpenTabAndScrollToHash(props) {
    const [{ initialSelectedItem, initialSelectedItems }, setInitialItems] = useState({
      initialSelectedItem: props.initialSelectedItem || 0,
      initialSelectedItems: props.initialSelectedItems || [0],
    });
    const [mainId, itemsWithIds] = getWithIds({ id: props.id, items: props.items });
    const router = useRouter();
    const uri = new URI(router.asPath);
    const urihash = uri.hash();

    const hashChangeHandler = (hash) => {
      const ids = itemsWithIds.map(({ headerId = '' }) => `#${upperCase(headerId)}`);
      const index = ids.indexOf(`#${upperCase(hash)}`);

      if (index !== -1) {
        setInitialItems({ initialSelectedItem: index, initialSelectedItems: [index] });
        scrollToElement(`#${mainId}`, 100, undefined, false);
      } else {
        setInitialItems({ initialSelectedItem: 0, initialSelectedItems: [0] });
      }
    };

    useEffect(() => {
      if (urihash) {
        hashChangeHandler(urihash);
      }
    }, [urihash]);

    return (
      <WrappedComponent
        {...props}
        id={mainId}
        initialSelectedItems={initialSelectedItems}
        initialSelectedItem={initialSelectedItem}
        setInitialItems={setInitialItems}
        items={itemsWithIds}
        key={mainId} // to reinitialize state when it is new tabs, see : https://fr.reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
      />
    );
  };
}
